import { Fragment } from 'react';
import Link from 'next/link';
import { makeStyles } from 'tss-react/mui';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import MaterialLinkButton from './material-link-button';
import SocialLinks from './social-links';
import Markdown from './markdown';
import SectionHeader from './section-header';
import FollowToggle from './users/follow-toggle';
import type { User } from '../types/User';

type Props = {
  user: User;
};

const useStyles = makeStyles()((theme) => ({
  hostContent: {
    display: 'flex',
    alignItems: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  hostPhoto: {
    paddingRight: 15,
    [theme.breakpoints.down('sm')]: {
      paddingRight: 0,
    },
  },
  hostSummary: {
    flexGrow: 1,
    '& > p:first-of-type': {
      marginTop: 0,
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(2),
    },
  },
  avatar: {
    width: 100,
    height: 100,
  },
  chips: {
    margin: theme.spacing(2, 0),
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(1),
    },
  },
}));

const HostUserSummary = ({ user }: Props) => {
  const { classes } = useStyles();
  return (
    <Fragment>
      <SectionHeader action={<FollowToggle user={user} size="small" />}>
        About {user.displayName}
      </SectionHeader>
      <Paper sx={{ p: 3, mb: 3 }}>
        <div className={classes.hostContent}>
          <div className={classes.hostPhoto}>
            <Avatar
              alt={user.displayName}
              src={user.avatar.med.url}
              className={classes.avatar}
            />
          </div>
          <div className={classes.hostSummary}>
            <Markdown>{user.summary}</Markdown>
            <SocialLinks source={user} />
            {user.pastHostedEventsCount > 0 && (
              <div className={classes.chips}>
                <Chip
                  size="small"
                  label={`${user.pastHostedEventsCount} past event${user.pastHostedEventsCount !== 1 ? 's' : ''}`}
                  variant="outlined"
                  component={Link}
                  href={`/people/${user.username}/past`}
                  clickable
                />
              </div>
            )}
            <MaterialLinkButton
              href={`/people/${user.username}`}
              variant="outlined"
            >
              {user.displayName}
            </MaterialLinkButton>
          </div>
        </div>
      </Paper>
    </Fragment>
  );
}

export default HostUserSummary;
